import { Pagination, Space } from "antd";
import { apiV2Root } from "consts";
import bem from "easy-bem";
import type {
  CommentFeedItem,
  CommentContentType,
} from "features/comments/data/types";
import Comment from "features/comments/elements/Comment";
import { buildContentRoute } from "features/links/utils/shortLink";
import { useDataQueryParams } from "hooks/useDataQueryParams";
import { useFlag } from "hooks/useFlags";
import { useUser } from "hooks/useUser";
import React, { useEffect, useState } from "react";
import { Meta, PaginatedResponse } from "types/basic";
import TabBar, { TabBarOption } from "ui/TabBar";

import "./CommentsFeed.css";

const cn = bem("comments-feed");

type FeedTab =
  | "combined"
  | "own"
  | "replies"
  | "mentions"
  | "my_content"
  | "moderator"
  | "all";

// Ленты «про себя»: персону сервер берёт из сессии, person_id не нужен
const SELF_ONLY_TABS: FeedTab[] = ["combined", "my_content", "moderator"];

const getTabOptions = (isOwnFeed: boolean): TabBarOption<FeedTab>[] => [
  { value: "combined", label: "Все" },
  { value: "own", label: isOwnFeed ? "Мои комментарии" : "Комментарии" },
  { value: "replies", label: isOwnFeed ? "Ответы мне" : "Ответы" },
  { value: "mentions", label: "Упоминания" },
  { value: "my_content", label: "К моему контенту" },
  { value: "moderator", label: "От модератора" },
  { value: "all", label: "Все комментарии" },
];

const contentTypeLabels: Record<Exclude<CommentContentType, null>, string> = {
  problem: "Проблема",
  solution: "Решение",
  attributecard: "Карточка атрибута",
  person: "Профиль",
};

const toMeta = (item: CommentFeedItem): Meta => {
  return {
    author: item.author?.id ?? null,
    user: item.user?.id ?? 0,
    dateCreated: item.dateCreated,
    dateModified: item.dateModified,
    isDraft: item.isDraft,
    isDeleted: item.isDeleted,
    likeCount: 0,
    likedByUser: false,
    favoriteCount: 0,
    favoritedByUser: false,
    reviewCount: 0,
    reviewedByUser: false,
  };
};

const getTargetText = (item: CommentFeedItem): string | null => {
  if (!item.contentType || !item.objectId) {
    return null;
  }

  return `${contentTypeLabels[item.contentType]} #${item.objectId}`;
};

const getObjectLink = (item: CommentFeedItem): string | null => {
  if (!item.contentType || !item.objectId) {
    return null;
  }

  // person — диалог с модерацией привязан к профилю целиком, а не к
  // конкретному комментарию: ведём на сам профиль (там же виден диалог).
  if (item.contentType === "person") {
    const route = buildContentRoute({ kind: "person", id: item.objectId });

    return `${window.location.origin}/#${route.pathname}${route.hash}`;
  }

  if (!item.problemId) {
    return null;
  }

  const route = buildContentRoute({
    kind: "comment",
    id: item.id,
    problemId: item.problemId,
    solutionId: item.solutionId ?? undefined,
    cardId:
      item.contentType === "attributecard"
        ? (item.objectId ?? undefined)
        : undefined,
  });

  return `${window.location.origin}/#${route.pathname}${route.hash}`;
};

type CommentsFeedProps = {
  personId?: number;
  isModeratorial?: boolean;
};

const CommentsFeed: React.FC<CommentsFeedProps> = ({
  personId,
  isModeratorial = false,
}) => {
  const { personId: currentUserPersonId, isModerator } = useUser();
  const moderationEnabled = useFlag("moderationEnabled");

  const isOwnFeed = !personId || personId === currentUserPersonId;
  const targetPersonId = personId ?? currentUserPersonId ?? undefined;

  // По умолчанию — «Все» в своём профиле, иначе первая доступная вкладка.
  // Состояние nullable, чтобы дефолт следовал за isOwnFeed, пока пользователь
  // сам не выбрал вкладку (данные о пользователе могут подгрузиться позже).
  const defaultTab: FeedTab = isModeratorial
    ? "all"
    : isOwnFeed
      ? "combined"
      : "own";
  const [selectedTab, setSelectedTab] = useState<FeedTab | null>(null);
  const activeTab = selectedTab ?? defaultTab;
  const [page, setPage] = useState(1);

  useEffect(() => {
    setPage(1);
  }, [activeTab]);

  const isSelfOnlyTab = SELF_ONLY_TABS.includes(activeTab);
  const isModeratorTab = activeTab === "moderator";

  const feedResponse = useDataQueryParams<PaginatedResponse<CommentFeedItem>>(
    "comments",
    {
      feed: activeTab,
      person_id:
        activeTab === "all" || isSelfOnlyTab ? undefined : targetPersonId,
      page,
      // Переписка с модерацией читается сверху вниз — порядок оставляем серверу
      ordering: isModeratorTab ? undefined : "-dateCreated",
    },
    apiV2Root,
  );

  if (isModeratorial && !(isModerator && moderationEnabled)) {
    return (
      <p>
        Ошибка при загрузке данных. Возможно, у вас недостаточно прав для
        просмотра этого контента.
      </p>
    );
  }

  // «Все», свой контент и переписка с модерацией — только в своём профиле
  const visibleTabs: FeedTab[] = isModeratorial
    ? ["all"]
    : isOwnFeed
      ? ["combined", "own", "replies", "mentions", "my_content", "moderator"]
      : ["own", "replies", "mentions"];

  if (!feedResponse) {
    return <div>Загрузка ленты комментариев...</div>;
  }

  const items = feedResponse.results || [];

  const visibleTabOptions: TabBarOption<FeedTab>[] = getTabOptions(
    isOwnFeed,
  ).filter((option) => visibleTabs.includes(option.value));

  const handleTabChange = (value: FeedTab) => setSelectedTab(value);

  return (
    <div className={cn()}>
      <h3>
        {isModeratorial
          ? "Модерация комментариев"
          : isModeratorTab
            ? // Не лента комментариев, а личная переписка с модерацией
              "Личные сообщения с модерацией"
            : isOwnFeed
              ? "Моя лента комментариев"
              : "Лента комментариев"}
      </h3>

      {visibleTabs.length > 1 && (
        <div className={cn("tabs")}>
          <TabBar
            value={activeTab}
            options={visibleTabOptions}
            onChange={handleTabChange}
          />
        </div>
      )}

      {items.length === 0 ? (
        <div>
          {isModeratorTab
            ? "Модерация вам не писала."
            : "В этом разделе пока нет комментариев."}
        </div>
      ) : (
        <Space
          direction="vertical"
          size="middle"
          style={{ width: "100%" }}
        >
          {items.map((item) => {
            const mentionedPeople = (item.mentions || []).map((person) => {
              return (
                [person.firstName, person.secondName]
                  .filter(Boolean)
                  .join(" ") || `Пользователь ${person.id}`
              );
            });
            const targetText = getTargetText(item);
            const objectLink = getObjectLink(item);

            return (
              <div
                key={item.id}
                className={cn("item")}
              >
                <Comment
                  id={item.id}
                  text={item.content}
                  meta={toMeta(item)}
                  mentionedPeople={mentionedPeople}
                />

                {targetText && (
                  <div className={cn("item-target")}>
                    Объект:{" "}
                    {objectLink ? (
                      <a href={objectLink}>{targetText}</a>
                    ) : (
                      targetText
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </Space>
      )}

      {feedResponse.totalPages > 1 && (
        <div className={cn("pagination")}>
          <Pagination
            current={feedResponse.page}
            pageSize={feedResponse.pageSize}
            total={feedResponse.count}
            onChange={setPage}
            showSizeChanger={false}
          />
        </div>
      )}
    </div>
  );
};

export default CommentsFeed;
